import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, type AppId } from "./api";

export interface ConnOption {
  connId: string;
  name: string;
  envLabel: string;
  production: boolean;
  usable: boolean;
  reason?: string;
}
export interface AppConnections {
  name: string;
  active: string | null;
  options: ConnOption[];
}

interface Ctx {
  apps: Partial<Record<AppId, AppConnections>>;
  loaded: boolean;
  setActive: (app: AppId, connId: string) => Promise<void>;
  reload: () => Promise<void>;
}

const ConnectionsContext = createContext<Ctx>({ apps: {}, loaded: false, setActive: async () => undefined, reload: async () => undefined });

/** The signed-in user's active connection per app (design §5.3), shared by the header picker, launcher and apps. */
export function ConnectionsProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [apps, setApps] = useState<Ctx["apps"]>({});
  const [loaded, setLoaded] = useState(false);
  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      setApps((await api<{ apps: Ctx["apps"] }>("/me/connections")).apps);
    } finally {
      setLoaded(true);
    }
  }, [enabled]);
  useEffect(() => {
    void reload();
    // Mappings and entitlements change while people are signed in: re-read when the window comes back into focus.
    const onFocus = () => void reload();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [reload]);
  const setActive = useCallback(async (app: AppId, connId: string) => {
    await api(`/me/connections/${app}`, { method: "PUT", body: { connId } });
    setApps((cur) => ({ ...cur, [app]: cur[app] && { ...cur[app]!, active: connId } }));
  }, []);
  return <ConnectionsContext.Provider value={{ apps, loaded, setActive, reload }}>{children}</ConnectionsContext.Provider>;
}

export const useConnections = () => useContext(ConnectionsContext);

/** The active connection option for an app, or null. */
export function activeOption(c: AppConnections | undefined) {
  return c?.options.find((o) => o.connId === c.active) ?? null;
}
